import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { OrganizationQuery } from '@core/organization/organization.query';
import { AppStoreQuery } from '@core/state';
import {
  investorRoute,
  loginAbsoluteRoute,
  pendingAreaRoute,
  productRoute,
  selectCompanyAbsoluteRoute,
} from '@shared/shared-routes';
import { Observable, combineLatest } from 'rxjs';
import { first, map } from 'rxjs/operators';

import { AuthorizationService } from './authorization.service';
import { OrganizationType } from '@core/models/organization.model';
import { User } from '@core/models/user.model';

@Injectable({
  providedIn: 'root',
})
export class IsSuperUserGuard {
  constructor(
    private router: Router,
    private appStoreQuery: AppStoreQuery,
    private organizationQuery: OrganizationQuery,
    private authorization: AuthorizationService,
  ) {}

  public canActivate(): Observable<boolean | UrlTree> {
    return combineLatest([
      this.appStoreQuery.selectUser(),
      this.organizationQuery.selectChosenOrganizationType(),
    ]).pipe(
      map(([user, orgType]) => this.resolveAccess(user, orgType)),
      first(),
    );
  }

  public canLoad(): Observable<boolean> | Promise<boolean> | boolean {
    return this.appStoreQuery.selectUser().pipe(
      map((user) => this.authorization.canAccessSuperUserArea(user)),
      first(),
    );
  }

  private resolveAccess(user: User | null | undefined, orgType: OrganizationType | null): boolean | UrlTree {
    if (user === null || user === undefined) {
      return this.router.parseUrl(loginAbsoluteRoute);
    }

    if (user.isRestrictedUser) {
      return this.router.parseUrl(`/${pendingAreaRoute}`);
    }

    if (this.authorization.canAccessSuperUserArea(user)) {
      return true;
    }

    return this.router.parseUrl(this.getFallbackRoute(user, orgType));
  }

  private getFallbackRoute(user: User, orgType: OrganizationType | null): string {
    if (user.isClient && user.client?.uid) {
      return `/client/${user.client.uid}/marketplace`;
    }

    if (this.authorization.isEmittentCompany(orgType)) {
      return `/${productRoute}`;
    }

    if (this.authorization.isInvestorCompany(orgType)) {
      return `/${investorRoute}`;
    }

    return `/${selectCompanyAbsoluteRoute}`;
  }
}
